import { G2S } from '../g2s';
import * as api from '../api';
import * as flights from '../flights';
import * as utils from '../utils';

declare let ga: any;

export class GoogleAnalyticsService {
    private affiliate = '';

    addFlightProduct(flight: flights.FlightModel, passengerCount: number) {
        const segmentFirst = flight.legs[0].segments[0];
        const segmentLast = flight.legs[0].segments[flight.legs[0].segments.length - 1];
        const productId = 'A-' + segmentFirst.departureDestination.iataCode
            + '-' + segmentLast.arrivalDestination.iataCode
            + '-' + flight.validatingAirline.iataCode;
        const productName = 'AirTickets ' + segmentFirst.departureDestination.iataCode
            + (flight.legs.length == 1 ? '>' : '<>')
            + segmentLast.arrivalDestination.iataCode
            + ' Class-' + flight.bookingClass
            + ' Airline-' + flight.validatingAirline.iataCode;

        ga('ec:addProduct', {
            'id': productId,
            'name': productName,
            'category': 'ticket.air',
            'brand': flight.validatingAirline.iataCode,
            'variant': flight.bookingClass,
            'price': flight.billingPrice,
            'quantity': passengerCount
        });
    }

    addProduct(product: flights.ProductModel, passengerCount: number) {
        const productId = 'P-' + product.product.type + '-' + product.product.id;
        const productName = 'Product.' + product.product.externalId + ' ID-' + product.product.id;

        ga('ec:addProduct', {
            'id': productId,
            'name': productName,
            'category': 'product.' + product.product.type.toLowerCase(),
            'brand': product.product.adapter ? product.product.adapter : 'Go2See',
            //'variant': '',
            'price': product.product.price,
            'quantity': product.product.priceType == 'PerPassenger' ?  passengerCount : 1
        });
    }

    addSearchParams(gaParams: utils.GoogleAnalyticsParams, searchParams: flights.FlightSearchParams) {
        if (gaParams && searchParams) {
            gaParams.dimension4 = searchParams.roundtrip ? 'Round Trip' : 'One Way';
            gaParams.dimension5 = searchParams.from + '-' + searchParams.to;
            gaParams.dimension6 = searchParams.class;
            gaParams.dimension7 = searchParams.passengers.adults + '-' + searchParams.passengers.children + '-' + searchParams.passengers.infants;
            gaParams.metric3 = searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants;
        }
    }

    setAction(actionName: string, actionData: any) {
        ga('ec:setAction', actionName, actionData);
    }

    sendPageview(page: string, title: string, fields?: GoogleAnalyticsParams) {
        if (ga) {
            const f: any = {};

            if (page) {
                f.page = page;
            }
            if (title) {
                f.title = title;
            }

            if (fields) {
                for (const nextKey in fields) {
                    f[nextKey] = fields[nextKey];
                }
            }

            if (G2S.config.instance) {
                f['dimension12'] = G2S.config.instance;
            }

            if ((<any>window).__gaClientID) {
                f['dimension14'] = (<any>window).__gaClientID;
            }

            if (!this.affiliate
                && utils.Cookies.getPartnerCookie()) {
                // Request dimension8 affiliate value from server
                new api.UtilsService().getAffiliateInfo(utils.Cookies.getPartnerCookie()).then(
                    (result: string) => {
                        this.affiliate = result;

                        if (this.affiliate) {
                            f['dimension8'] = this.affiliate;
                        }

                        ga('send', 'pageview', f);

                        if (G2S.isDev) {
                            console.log('GA pageview ' + page + '.', f);
                        }

                        // Yandex metrica
                        if (window['ym']
                            && window['__ymCounterId']) {
                            window['ym'](window['__ymCounterId'], 'hit', page, { title: title });
                            if (G2S.isDev) {
                                console.log('YM hit ' + page + '.', f);
                            }
                        }
                    },
                    () => {
                        ga('send', 'pageview', f);

                        if (G2S.isDev) {
                            console.log('GA pageview ' + page + '.', f);
                        }

                        // Yandex metrica
                        if (window['ym']
                            && window['__ymCounterId']) {
                            window['ym'](window['__ymCounterId'], 'hit', page, { title: title });
                            if (G2S.isDev) {
                                console.log('YM hit ' + page + '.', f);
                            }
                        }
                    }
                );
            } else {
                if (this.affiliate) {
                    f['dimension8'] = this.affiliate;
                }

                ga('send', 'pageview', f);

                if (G2S.isDev) {
                    console.log('GA pageview ' + page + '.', f);
                }

                // Yandex metrica
                if (window['ym']
                    && window['__ymCounterId']) {
                    window['ym'](window['__ymCounterId'], 'hit', page, { title: title });
                    if (G2S.isDev) {
                        console.log('YM hit ' + page + '.', f);
                    }
                }
            }
        }
    }

    reachGoal(label: string, params?: (string | number | boolean)[]) {
        if (window['ym']
            && window['__ymCounterId']) {
                if (params) {
                    window['ym'](window['__ymCounterId'], 'reachGoal', label, params);
                } else {
                    window['ym'](window['__ymCounterId'], 'reachGoal', label);
                }
        }
        if (G2S.isDev) {
            console.log('YM reach goal ' + label);
        }
    }

    sendEvent(category: string, action: string, label: string, fields?: GoogleAnalyticsParams) {
        if (ga) {
            const f: any = {};

            if (fields) {
                for (const nextKey in fields) {
                    f[nextKey] = fields[nextKey];
                }
            }

            // Affiliate will be initialized by first sendPageview, so there's no need to request it from server here
            if (this.affiliate) {
                f['dimension8'] = this.affiliate;
            }

            if (G2S.config.instance) {
                f['dimension12'] = G2S.config.instance;
            }

            if ((<any>window).__gaClientID) {
                f['dimension14'] = (<any>window).__gaClientID;
            }

            ga('send', 'event', category, action, label, f);

            if (G2S.isDev) {
                console.log('GA event ' + category + ' / ' + action + ' / ' + label + '.', f);
            }
        }
    }
}

export class GoogleAnalyticsParams {
    /**
     * Payment Adapter
     */
    dimension1?: string;
    /**
     * GDS Adapter
     */
    dimension2?: string;
    /**
     * Airline
     */
    dimension3?: string;
    /**
     * Flight Type
     */
    dimension4?: string;
    /**
     * Flight Direction
     */
    dimension5?: string;
    /**
     * Flight Class
     */
    dimension6?: string;
    /**
     * Flight Passengers
     */
    dimension7?: string;
    /**
     * Affiliate
     */
    dimension8?: string;
    /**
     * Coupon ID
     */
    dimension9?: string;
    /**
     * MultiFR
     */
    dimension10?: string;
    /**
     * Promotion ID
     */
    dimension11?: string;
    /**
     * a/b test
     */
    dimension12?: string;
    /**
     * Ga client ID
     */
    dimension14?: string;
    /**
     * Flights Search Duration
     */
    metric1?: number;
    /**
     * Flights Search Results Count
     */
    metric2?: number;
    /**
     * Passenger Count
     */
    metric3?: number;
    /**
     * Api Flight Search Count
     */
    metric4?: number;
    /**
     * GDS Search Count
     */
    metric5?: number;
    /**
     * Segment Count
     */
    metric6?: number;
    /**
     * Days to departure
     */
    metric7?: number;
    /**
     * Reservation opinion
     */
    metric8?: number;
    /**
     * Flight InitStart Duration
     */
    metric9?: number;
}
